import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Tooltip } from "@mui/material";
import ArrowCircleRightOutlinedIcon from '@mui/icons-material/ArrowCircleRightOutlined';
import MaterialReactTable from "material-react-table";
import { SiteDTO, uploadSites, exportSitesData } from "api";
import { PlusIcon } from "assets";
import { COLORS, Loading, Pagination } from "components";
import { RecUploadButton } from "components/buttons/rec-upload-button";
import { ExportButton } from "components/buttons/exportButton";
import { useGetAllowedValues, useNotify, usePagination, useSitesPaginated } from "hooks";
import { QUERY_KEYS } from "consts";
import { USER_ROLES, useNewBreadcrumb, useUserRole } from "recoils";
import { SiteColumns } from "./components/site-editor-form/components/SiteColumns";
import { SITES_PAGE } from "./consts";
import { AddSiteModal } from "./components/site-editor-form/components/AddSiteModal";
import { getAllowedValues, ActiveFilters } from "./components";
import { useSiteFilters } from "./hooks";

export const TabSite = ({ state }: { state: string }) => {
  const notify = useNotify();
  const navigate = useNavigate();
  const isActiveTab = state === 'active';
  const localStoragePaginationKey = `site-management-${isActiveTab ? 'activeTab' : 'inactiveTab'}-pagination`;

  useNewBreadcrumb(SITES_PAGE.TITLE);

  const { pagination, paginatedQuery, onPageChange, onPageSizeChange, updateFromApi } = usePagination({
    localStorageKey: localStoragePaginationKey,
    initialPage: 0,
    initialSize: 50,
  });
  const localStorageFiltersKey = `site-management-${isActiveTab ? 'activeTab' : 'inactiveTab'}-filters`;
  const { appliedFilters, applyFilters, clearFilters, clearSpecificFilter } = useSiteFilters({ localStorageKey: localStorageFiltersKey });
  const { data: sitesData, isLoading, isFetching } = useSitesPaginated({ ...paginatedQuery, isActive: isActiveTab }, appliedFilters);
  const sites = sitesData?.sites ?? [];
  const { data: allowedValues } = useGetAllowedValues();
  const userRole = useUserRole();
  const [addSiteModalOpen, setAddSiteModalOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<any[]>([]);

  // Map applied filters to column filter format
  const getColumnFiltersFromApplied = () => Object.entries(appliedFilters)
    .filter(([, value]) => value !== null && value !== '')
    .map(([key, value]) => {
      // Map filter keys to column IDs
      const columnIdMap: { [key: string]: string } = {
        customerSiteId: 'customerSiteId',
        name: 'name',
        type: 'type',
        businessUnit: 'businessUnit',
        city: 'city',
        country: 'country',
        scope2region: 'countryRegion',
        status: 'status',
        userName: 'firstName'
      };

      return {
        id: columnIdMap[key] || key,
        value: value as string
      };
    });

  const hasPendingFilters = columnFilters.length > 0 &&
    columnFilters.some(filter => filter.value && filter.value !== '');

  useEffect(() => {
    if (sitesData?.pageResult) {
      updateFromApi(sitesData.pageResult);
    }
  }, [sitesData?.pageResult, updateFromApi]);

  useEffect(() => {
    setColumnFilters(getColumnFiltersFromApplied());
  }, [appliedFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseSiteModal = () => {
    if (addSiteModalOpen) {
      setAddSiteModalOpen(false);
    }
  };

  const handleNavigateToSiteDetails = (siteData: SiteDTO) => {
    if (siteData) {
      navigate(`/sites/site-details/${siteData.id}`);
    } else {
      notify.error("Site id not found.");
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 4,
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <RecUploadButton
            label="Upload Data"
            uploadApiCall={uploadSites}
            queryKeyToInvalidate={QUERY_KEYS.SITES}
            availableToRoles={[USER_ROLES.MODULE_ADMIN, USER_ROLES.DATA_PROVIDER, USER_ROLES.DATA_VALIDATOR, USER_ROLES.DATA_REVIEWER]}
            extraReqParams={[["configuration", "true"]]}
          />
          <Button
            sx={{ height: "36px", width: "140px", borderRadius: 2, marginTop: "10px" }}
            variant="contained"
            onClick={() => setAddSiteModalOpen(true)}
            startIcon={<PlusIcon />}
            disabled={userRole.isAuditor || userRole.isDataProvider}
          >
            Add Site
          </Button>
          <ExportButton
            sx={{ height: "36px", width: "200px", borderRadius: 2, marginTop: "10px" }}
            exportFunction={exportSitesData}
            exportParams={{}}
            exportTitle="site_management_export"
            exportText="Export to Excel"
            exportingText="Loading..."
            successMessage="Export completed successfully!"
            errorMessage="Failed to Export to Excel"
            isZipFile={false}
            fileExtension=".xlsx"
          />
        </Box>
        {(hasPendingFilters || Object.values(appliedFilters).some(v => v !== null)) && (
          <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
            <Button
              sx={{
                height: "36px",
                width: "120px",
                borderRadius: 2,
                marginTop: "10px",
                ...(hasPendingFilters && {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  }
                })
              }}
              variant={hasPendingFilters ? "contained" : "outlined"}
              onClick={() => {
                applyFilters(columnFilters);
                setColumnFilters([]); // Clear column filters after applying
              }}
              disabled={!hasPendingFilters}
            >
              Apply Filters
            </Button>
            <Button
              sx={{ height: "36px", width: "100px", borderRadius: 2, marginTop: "10px" }}
              variant="outlined"
              onClick={() => {
                clearFilters();
                setColumnFilters([]);
              }}
              disabled={Object.values(appliedFilters).every(v => v === null)}
            >
              Clear All
            </Button>
          </Box>
        )}
      </Box>
      <ActiveFilters
        appliedFilters={appliedFilters}
        onClearFilter={clearSpecificFilter}
      />
      {isFetching || isLoading ? (
        <Loading />
      ) : (
        <>
          <MaterialReactTable
            state={{ isLoading }}
            enableSorting
            enablePagination
            manualPagination
            rowCount={pagination.size}
            getRowId={(row: any) => row.id}
            columns={SiteColumns}
            enableGlobalFilter={false}
            enableColumnFilters
            manualFiltering
            onColumnFiltersChange={(updater) => {
              const newFilters = typeof updater === 'function' ? updater([]) : updater;
              // Store filters but don't apply immediately
              setColumnFilters(newFilters);
            }}
            muiTableHeadCellFilterTextFieldProps={{
              onKeyDown: (event) => {
                if (event.key === 'Enter') {
                  applyFilters(columnFilters);
                  setColumnFilters([]); // Clear column filters after applying
                }
              },
              InputProps: {
                endAdornment: (
                  <Tooltip title="Press Enter or click to apply filter">
                    <Box
                      component="span"
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 4px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          transform: 'scale(1.1)',
                        }
                      }}
                      onClick={() => {
                        applyFilters(columnFilters);
                        setColumnFilters([]); // Clear column filters after applying
                      }}
                    >
                      <ArrowCircleRightOutlinedIcon
                        sx={{
                          fontSize: '20px',
                          color: hasPendingFilters ? 'primary.main' : 'text.secondary'
                        }}
                      />
                    </Box>
                  </Tooltip>
                ),
              },
            }}
            initialState={{
              columnFilters: getColumnFiltersFromApplied()
            }}
            data={sites}
            muiTableContainerProps={{
              sx: {
                "& thead": {
                  position: "sticky",
                  top: 0,
                  zIndex: 9,
                },
                "& thead tr:first-of-type th:first-of-type > div > div > div": {
                  minWidth: "36px",
                  width: "36px",
                },
                "& thead tr:first-of-type th:nth-child(2)": {
                  minWidth: "160px",
                  width: "160px",
                },
                "& thead tr:first-of-type th:nth-child(4)": {
                  minWidth: "190px",
                  width: "190px",
                },
                "& thead tr:first-of-type th:nth-child(5)": {
                  minWidth: "140px",
                  width: "140px",
                },
                "& tbody tr:nth-child(even)": {
                  height: 0,
                },
                "& tbody": {
                  maxWidth: '100%',
                  "& tr:nth-child(even) > td:first-of-type > div: first-of-type > div > div": {
                    width: 'calc(100vw - 200px)',
                  },
                },
                "& tbody tr td:first-of-type": {
                  paddingLeft: "20px",
                },
                "& tbody tr:first-of-type td > p": {
                  display: "flex",
                  width: "100%",
                  justifyContent: "center",
                },
                height: "calc(100vh - 300px)",
                border: "1px solid #D9D9D9",
                borderRadius: "10px",
                width: "100%",
              },
            }}
            muiTablePaperProps={{
              sx: {
                borderRadius: "10px",
                boxShadow: "none",
              },
            }}
            muiTopToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiBottomToolbarProps={{
              sx: {
                display: "none",
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                color: COLORS.Mainblue,
                lineHeight: "30px",
                "& .MuiBadge-root": {
                  display: "none",
                },
              },
            }}
            muiTableHeadRowProps={{
              sx: {
                height: "45px",
                backgroundColor: "#F8FAFC",
              },
            }}
            muiTableBodyRowProps={({ row }) => ({
              onClick: () => handleNavigateToSiteDetails(row.original),
              sx: {
                height: "45px",
                cursor: 'pointer',
                backgroundColor: "white",
                minWidth: '100%',
                width: '100%',
                maxWidth: '100%',
                '@media (min-width: 600px)': {
                  width: '100%',
                },
                '@media (min-width: 960px)': {
                  width: '1180px',

                },
                '@media (min-width: 1280px)': {
                  width: '1140px',

                },
                '@media (min-width: 1440px)': {
                  width: '1290px',

                },
                '@media (min-width: 1680px)': {
                  width: '1540px',

                },
                '@media (min-width: 2400px)': {
                  width: '100%',

                },
              },
            })}
            positionActionsColumn="last"
            defaultColumn={{
              minSize: 20,
              maxSize: 9001,
              size: 320,
            }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "10px",
            }}
          >
            <Pagination
              page={pagination.page + 1}
              rowsCount={pagination.totalRows}
              rowsPerPage={pagination.size}
              rowsPerPageOptions={[50, 100, 200]}
              onPageChange={onPageChange}
              onRowsPerPageChange={onPageSizeChange}
            />
          </Box>
        </>
      )}
      <AddSiteModal
        isOpen={addSiteModalOpen}
        onClose={handleCloseSiteModal}
        allowedValues={getAllowedValues(allowedValues)}
      />
    </Box>
  );
};
